import { Skeleton } from "@design";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { chatAttachmentFile } from "@/features/chat/documents/previewFile";
import { useDocumentText } from "@/features/chat/documents/textDocuments";
import { DraftCard } from "../DraftCard";
import { useDraftContext } from "../DraftContext";
import type { EntityDraftCardProps } from "../registry";
import { PublishDataAccess } from "./PublishDataAccess";
import { useMicroAppDraft } from "./useMicroAppDraft";

// conventions-ignore: no-raw-colors a web page's own content assumes a white page, in light and dark mode.
const FRAME_CLASS = "h-64 w-full rounded-md border border-border-subtle bg-white md:h-80";

/** A micro-app draft: the generated page, previewed, and Publish (or Republish for an existing app). */
export function MicroAppDraftCard({ draft, state, entityUuid, updatedAt, answerable }: Readonly<EntityDraftCardProps>) {
  const { t } = useTranslation("buildChat");
  const { linked } = useDraftContext();
  const app = useMicroAppDraft(draft);
  const name = app.body.name ?? linked.find((e) => e.uuid === draft.targetUuid)?.name ?? t("draft.untitled");
  return (
    <>
      <DraftCard
        title={name}
        entityLabel={t("draft.entity.micro_app")}
        state={state}
        isUpdate={Boolean(draft.targetUuid)}
        doneUuid={app.doneUuid}
        entityUuid={entityUuid}
        updatedAt={updatedAt}
        missingFields={draft.missingFields}
        questions={draft.questions}
        answerable={answerable}
        busy={app.busy}
        steps={app.steps}
        onCreate={app.run}
        onRetry={app.retry}
        createLabel={draft.targetUuid ? t("microApp.republish") : t("microApp.publish")}
        detailPath={(uuid) => `/build/apps/${uuid}/view`}
        summary={
          <div className="space-y-2">
            {app.body.description && <Text variant="body-sm">{app.body.description}</Text>}
            {app.body.attachmentUuid && <Preview attachmentUuid={app.body.attachmentUuid} title={name} />}
          </div>
        }
      />
      <PublishDataAccess
        sources={app.choosing}
        confirmLabel={draft.targetUuid ? t("microApp.republish") : t("microApp.publish")}
        onCancel={app.cancelChoice}
        onPublish={app.publishWith}
      />
    </>
  );
}

function Preview({ attachmentUuid, title }: Readonly<{ attachmentUuid: string; title: string }>) {
  const { t } = useTranslation("buildChat", { keyPrefix: "microApp" });
  const page = useDocumentText(chatAttachmentFile(attachmentUuid));
  if (page.isPending) return <Skeleton className="h-64 w-full rounded-md" />;
  if (page.isError) {
    return (
      <Text variant="body-sm" tone="tertiary">
        {t("noPreview")}
      </Text>
    );
  }
  // Generated HTML is untrusted: an empty sandbox runs no scripts and gets an opaque origin (as in Documents).
  return <iframe title={t("previewOf", { name: title })} sandbox="" srcDoc={page.data} className={FRAME_CLASS} />;
}
